/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React from 'react';
import styled from 'styled-components';
import { ThemeInterface } from '../../common/theme/AppThemeInterface';
import appTheme from '../../common/theme/appTheme.json';
import { HubsMarkerIcon, HubsSvgProps } from './HubsMarkerIcon';
import { HubDetails } from './HubsContainer';
import Scrollbars from '../../common/layout/Scrollbars';

const theme: ThemeInterface = appTheme;
const PADDING = 5;
const EXTRA_PADDING = 5;
const MAX_PANEL_WIDTH = 300;
const MAX_SCROLL_PANEL_HEIGHT = 245;

const ClusterNamesPanelWrapper = styled.div`
  position: absolute;
  max-width: ${MAX_PANEL_WIDTH}px;

  background-color: ${theme.primaryBackgroundColor};
  color: ${theme.primaryTextColor};
  font-family: ${theme.appFontFamily};

  .header {
    background-color: ${theme.secondaryBackgroundColor};
    padding: ${PADDING}px;
    padding-right: ${PADDING + EXTRA_PADDING}px;
    font-weight: 600;
  }

  .header__text {
    display: inline-block;
    padding-left: ${EXTRA_PADDING}px;

    font-size: 14px;
    line-height: 32px;
    vertical-align: top;
  }

  .content {
    padding: ${PADDING}px;
    padding-right: ${PADDING + EXTRA_PADDING}px;

    .hubs__cluster__names__panel__content__scroll {
      padding: 0 0 16px 0;
      box-sizing: border-box;
      max-height: none !important;
      margin-right: 15px;

      div {
        overflow-x: visible !important;
      }
    }
  }

  .icon {
    display: inline-block;
  }

  .text {
    display: inline-block;
    padding-left: ${EXTRA_PADDING}px;

    color: ${theme.secondaryTextColor};
    font-size: 12px;
    line-height: 32px;
    vertical-align: top;

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: ${MAX_PANEL_WIDTH - PADDING * 2 - EXTRA_PADDING * 2 - theme.hubs.iconWidth}px;
  }
`;

export interface ClusterNamesPanelProps {
  hubDetailsArray: HubDetails[];
  iconProps: HubsSvgProps;
  left: number;
  top: number;
  mouseLeaveHandler: () => void;
}

export const ClusterNamesPanel = (props: ClusterNamesPanelProps): React.ReactComponentElement<'div', {}> => {
  const { hubDetailsArray, iconProps, left, top, mouseLeaveHandler } = props;

  const icon = HubsMarkerIcon(iconProps);
  const iconUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(icon)}`;

  return (
    <ClusterNamesPanelWrapper
      style={{
        left: left - PADDING,
        top: top - PADDING,
      }}
      onMouseLeave={mouseLeaveHandler}
    >
      <div className="header">
        <div className="icon">
          <img src={iconUrl} alt="" />
        </div>
        <div className="header__text">HUBS ({hubDetailsArray.length})</div>
      </div>

      <div className="content">
        <Scrollbars
          className="hubs__cluster__names__panel__content__scroll"
          autoHeight
          autoHeightMin={0}
          autoHeightMax={MAX_SCROLL_PANEL_HEIGHT}
        >
          {hubDetailsArray
            .sort((a: HubDetails, b: HubDetails) => (a.name === b.name ? 0 : a.name > b.name ? 1 : -1))
            .map((hubDetails: HubDetails) => {
              const hubIcon = HubsMarkerIcon(hubDetails.hubIconProps);
              const hubIconUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(hubIcon)}`;

              return (
                <div key={hubDetails.id}>
                  <div className="icon">
                    <img src={hubIconUrl} alt="" />
                  </div>
                  <div className="text">{hubDetails.name}</div>
                </div>
              );
            })}
        </Scrollbars>
      </div>
    </ClusterNamesPanelWrapper>
  );
};

export default ClusterNamesPanel;
